import { useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getAnalytics } from "firebase/analytics";
import { RootState } from "app/store";
import { AnalyticsEventType } from "app/types";
import {
  recordEvent,
  setAnalyticsUserId,
  usesNativeAnalytics,
  setAnalyticsUserProperties,
} from "app/analytics";
import { isProd } from "common/utils";
import { LoadingStatus } from "features/subscription/types";

export const useAnalytics = () => {
  const location = useLocation();
  const parent = useSelector((state: RootState) => state.user.parent);
  const plan = useSelector((state: RootState) => state.subscription.plan);
  const planStatus = useSelector((state: RootState) => state.subscription.status);

  useEffect(() => {
    try {
      // ブラウザと Amazon 版のアプリは、Web の Firebase を最初に用意する
      if (!usesNativeAnalytics()) {
        getAnalytics();
      }
    } catch (error) {
      console.warn("Google Analytics Error.");
      console.error(error);
    }
    // recordEvent がその時のパスを付けるので、リスナーは最初に 1 回登録するだけでよい
    const handleVisibilityState = () => {
      recordEvent(
        document.visibilityState === "visible"
          ? AnalyticsEventType.ACTIVE
          : AnalyticsEventType.BACKGROUND,
      );
    };
    document.addEventListener("visibilitychange", handleVisibilityState);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityState);
    };
  }, []);

  useEffect(() => {
    if (parent?.uid) {
      setAnalyticsUserId(parent.uid);
    }
  }, [parent?.uid]);

  useEffect(() => {
    // プランを取得する前の既定値は送らない
    if (parent?.uid && planStatus === LoadingStatus.SUCCEEDED) {
      setAnalyticsUserProperties({ plan });
    }
  }, [parent?.uid, plan, planStatus]);

  // 今までどおり、Web の Firebase で送るとき（ブラウザ・Amazon 版）は最初の表示では送らず、パスが変わったときだけ送る
  const isFirstPageView = useRef(true);
  useEffect(() => {
    const isFirst = isFirstPageView.current;
    isFirstPageView.current = false;
    if (isFirst && !usesNativeAnalytics()) return;
    if (isProd) {
      recordEvent("page_view");
    }
  }, [location]);
};
